/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useState } from 'react';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import getOverviewStats from '../../../domain/use_cases/overview/getOverviewStats';
import { generateOverviewChartData } from '../../../core/utils/commonUtils';
import getMessagesStats from '../../../domain/use_cases/overview/getMessagesStats';
import getCRMStats from '../../../domain/use_cases/overview/getCRMStats';
import SubscriptionDataSource from '../../../domain/enums/subscription/subscription_datasource/SubscriptionDataSource';
import getOverviewPeriodStats from '../../../domain/use_cases/overview/getOverviewPeriodStats';
import { Dayjs } from 'dayjs';
import getSMSStats from '../../../domain/use_cases/overview/getSMSStats';

interface QualificationDistribution {
  interested: number;
  considering: number;
  not_interested: number;
  not_responsive: number;
}

interface ChartsData {
  series: ChartSeries[];
  categories: string[];
}

interface ChartSeries {
  data: number[];
  name: string;
}

const OverviewStatsContext = createContext({
  subscriptionsCount: 0,
  contactsCount: 0,
  engagedNotAvailableCount: 0,
  usersCount: 0,
  numbersCount: 0,
  jobsCount: 0,
  currentJobsCount: 0,
  smsCount: 0,
  currentSmsCount: 0,
  currentNotificationsCount: 0,
  totalPriceSms: 0,
  replyRate: 0,
  todaySMSStats: {} as { [key: string]: { planned: number; sent: number; received: number } },
  qualificationDistribution: {
    interested: 0,
    considering: 0,
    not_interested: 0,
    not_available: 0,
    not_responsive: 0,
  } as QualificationDistribution,
  chartsData: {
    series: [] as ChartSeries[],
    categories: [] as string[],
  },
  crmStats: {} as Record<SubscriptionDataSource, number>,
  fetch: () => {},
  fetchCharts: () => {},
  fetchCRMStats: () => {},
  fetchMessagesStats: (date: Dayjs) => {},
  fetchTodaySMSStats: (date: Dayjs) => {},
  reset: () => {},
  status: ContextStatus.IDLE,
  chartStatus: ContextStatus.IDLE,
  crmChartStatus: ContextStatus.IDLE,
  messagesStatus: ContextStatus.IDLE,
  todaySMSChartStatus: ContextStatus.IDLE,
});

export const OverviewStatsProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [subscriptionsCount, setSubscriptionsCount] = useState(0);
  const [contactsCount, setContactsCount] = useState(0);
  const [usersCount, setUsersCount] = useState(0);
  const [numbersCount, setNumbersCount] = useState(0);
  const [engagedNotAvailableCount, setEngagedNotAvailableCount] = useState(0);
  const [jobsCount, setJobsCount] = useState(0);
  const [currentJobsCount, setCurrentJobsCount] = useState(0);
  const [smsCount, setSMSCount] = useState(0);
  const [currentSmsCount, setCurrentSMSCount] = useState(0);
  const [currentNotificationsCount, setCurrentNotificationsCount] = useState(0);
  const [totalPriceSms, setTotalPriceSms] = useState(0);
  const [replyRate, setReplyRate] = useState(0);
  const [todaySMSStats, setTodaySMSStats] = useState(
    {} as { [key: string]: { planned: number; sent: number; received: number } }
  );
  const [chartsData, setChartsData] = useState<ChartsData>({
    series: [],
    categories: [],
  });
  const [qualificationDistribution, setQualificationDistribution] =
    useState<QualificationDistribution>({
      interested: 0,
      considering: 0,
      not_interested: 0,
      not_responsive: 0,
    });
  const [crmStats, setCrmStats] = useState(
    {} as Record<SubscriptionDataSource, number>
  );
  const [status, setStatus] = useState(ContextStatus.IDLE);
  const [chartStatus, setChartStatus] = useState(ContextStatus.IDLE);
  const [crmChartStatus, setCrmChartStatus] = useState(ContextStatus.IDLE);
  const [messagesStatus, setMessagesStatus] = useState(ContextStatus.IDLE);
  const [todaySMSChartStatus, setTodaySMSChartStatus] = useState(
    ContextStatus.IDLE
  );

  const fetch = async () => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getOverviewStats();
      setSubscriptionsCount(useCaseResponse.subscriptionsCount);
      setContactsCount(useCaseResponse.contactsCount);
      setEngagedNotAvailableCount(useCaseResponse.engagedNotAvailableCount);
      setUsersCount(useCaseResponse.usersCount);
      setNumbersCount(useCaseResponse.numbersCount);
      setJobsCount(useCaseResponse.jobsCount);
      setSMSCount(useCaseResponse.smsCount);
      setTotalPriceSms(useCaseResponse.totalPriceSms);
      setReplyRate(useCaseResponse.replyRate);
      setQualificationDistribution(useCaseResponse.qualificationDistribution);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const fetchCharts = async () => {
    setChartStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getMessagesStats(60);
      const chartsData = generateOverviewChartData(useCaseResponse);
      setChartsData(chartsData);
      setChartStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setChartStatus(ContextStatus.ERROR);
    }
  };

  const fetchCRMStats = async () => {
    setCrmChartStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getCRMStats();
      setCrmStats(useCaseResponse);
      setCrmChartStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setCrmChartStatus(ContextStatus.ERROR);
    }
  };

  const fetchTodaySMSStats = async (date: Dayjs) => {
    setTodaySMSChartStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getSMSStats(date);
      setTodaySMSStats(useCaseResponse);
      setTodaySMSChartStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setTodaySMSChartStatus(ContextStatus.ERROR);
    }
  };

  const fetchMessagesStats = async (date: Dayjs) => {
    setMessagesStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getOverviewPeriodStats(date);
      setCurrentJobsCount(useCaseResponse.currentJobsCount);
      setCurrentSMSCount(useCaseResponse.currentSmsCount);
      setCurrentNotificationsCount(useCaseResponse.currentNotificationsCount);
      setMessagesStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setMessagesStatus(ContextStatus.ERROR);
    }
  };

  const reset = () => {
    setSubscriptionsCount(0);
    setContactsCount(0);
    setEngagedNotAvailableCount(0);
    setUsersCount(0);
    setNumbersCount(0);
    setJobsCount(0);
    setCurrentJobsCount(0);
    setSMSCount(0);
    setCurrentSMSCount(0);
    setCurrentNotificationsCount(0);
    setTotalPriceSms(0);
    setReplyRate(0);
    setQualificationDistribution({
      interested: 0,
      considering: 0,
      not_interested: 0,
      not_responsive: 0,
    });
    setChartsData({
      series: [],
      categories: [],
    });
    setCrmStats({} as Record<SubscriptionDataSource, number>);
    setStatus(ContextStatus.IDLE);
    setChartStatus(ContextStatus.IDLE);
    setCrmChartStatus(ContextStatus.IDLE);
    setMessagesStatus(ContextStatus.IDLE);
  };

  return (
    <OverviewStatsContext.Provider
      value={{
        subscriptionsCount,
        contactsCount,
        engagedNotAvailableCount,
        usersCount,
        numbersCount,
        jobsCount,
        currentJobsCount,
        smsCount,
        currentSmsCount,
        currentNotificationsCount,
        totalPriceSms,
        replyRate,
        qualificationDistribution,
        chartsData,
        crmStats,
        fetch,
        fetchCharts,
        fetchCRMStats,
        fetchMessagesStats,
        reset,
        status,
        chartStatus,
        crmChartStatus,
        messagesStatus,
        todaySMSStats,
        fetchTodaySMSStats,
        todaySMSChartStatus,
      }}
    >
      {children}
    </OverviewStatsContext.Provider>
  );
};

export default OverviewStatsContext;
